import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { PermissionService } from '../../services/permission.service';
import { NtripConnection } from '../../models/ntrip-connection';
import { environment } from '../../../environments/environment';

interface Message {
  message: string;
}

@Component({
  selector: 'app-admin-tools',
  templateUrl: './admin-tools.component.html',
  styleUrls: ['./admin-tools.component.scss']
})
export class AdminToolsComponent implements OnInit {
  public accessToken: string = ''; 
  public decodedToken: any;
  public userPermission: string[] = [];
  public currentResponse: any;
  // Test by API key subsection state
  public apiKey: string = '';
  public apiKeyLoading: boolean = false;
  public apiKeyResultText: string = 'You can verify the API key access to the backend.';
  public allNtripConnections: NtripConnection[] = [];
  public loadingAllConnections = false;
  public isAdmin = false;
  // Encrypt by Config Server subsection state (admin-only)
  public encryptInput: string = '';
  public encryptLoading: boolean = false;
  public encryptResultText: string = 'Enter text to encrypt…';
  public encryptUrl: string = '';
  // Decrypt section state
  public decryptInput: string = '';
  public decryptLoading: boolean = false;
  public decryptResultText: string = 'Enter text to decrypt…';
  public decryptUrl: string = '';

  public$!: Observable<Message>;
  private$!: Observable<Message>;
  privateScoped$!: Observable<Message>;
  userinfo$!: Observable<Message>;

  constructor(private auth: AuthService, private http: HttpClient, private userService: UserService, private perms: PermissionService) {}

  ngOnInit(): void {
    this.checkAdminPermissions();
  }

  checkAdminPermissions(): void {
    this.perms.ensureFresh();
    this.userPermission = this.perms.getPermissions();
    this.isAdmin = this.perms.isAdmin();
    if (this.isAdmin) {
      this.getAllNtripConnections();
    }
  }

  getAllNtripConnections(): void {
    this.loadingAllConnections = true;
    this.userService.getAllNtripConnections().subscribe({
      next: (data: NtripConnection[]) => {
        this.allNtripConnections = data;
        this.loadingAllConnections = false;
      },
      error: (error) => {
        console.error('Failed to get all NTRIP connections:', error);
        this.loadingAllConnections = false;
      }
    });
  }

  retrieveUserPermissions(): void {
    this.perms.ensureFresh();
    this.userPermission = this.perms.getPermissions();
    this.currentResponse = this.userPermission;
  }

  showAccessToken(): void {
    this.accessToken = this.auth.getAccessToken();
    this.currentResponse = this.accessToken; // 将令牌存储到 currentResponse
  }

  showDecodedToken(): void {
    this.decodeToken(this.auth.getAccessToken());
  }

  decodeToken(token: string): void {
    if (!token) {
      console.error('Token is empty or invalid');
      return;
    }
    
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Token format is incorrect');
      return;
    }
    
    const payload = parts[1];
    const decodedJson = this.base64UrlDecode(payload);
    this.decodedToken = JSON.parse(decodedJson);
    this.currentResponse = this.decodedToken; // 将解码的令牌存储到 currentResponse
  }

  base64UrlDecode(str: string): string {
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    let binaryString = window.atob(base64);
    let output = decodeURIComponent(Array.from(binaryString).map((char) => 
      '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    return output;
  }

  request(): void {
    // 同时发起所有 API 调用，并存储响应以在界面上显示
    this.public$ = this.http.get<Message>(`${environment.apiBaseUrl}/public`);
    this.private$ = this.http.get<Message>(`${environment.apiBaseUrl}/private`);
    this.privateScoped$ = this.http.get<Message>(
      `${environment.apiBaseUrl}/private-scoped`
    );
    this.userinfo$ = this.http.get<Message>(
      `${environment.apiBaseUrl}/userinfo`
    );
  }

  // ---- Test by API key ----
  testByApiKey(): void {
    const url = `${environment.apiBaseUrl}/by-api-key`;
    this.apiKeyLoading = true;
    this.apiKeyResultText = 'Testing...';
    this.http.get(url, {
      headers: {
        'accept': '*/*',
        'secret': this.apiKey || ''
      },
      observe: 'response',
      responseType: 'text'
    }).subscribe({
      next: (res) => {
        const status = (res as any)?.status ?? 200;
        const body = (res as any)?.body ?? '';
        this.apiKeyResultText = `HTTP ${status}\n${body}`;
        this.apiKeyLoading = false;
      },
      error: (err) => {
        const status = err?.status;
        const body = typeof err?.error === 'string' ? err.error : (err?.message || 'Error');
        this.apiKeyResultText = `HTTP ${status ?? ''}\n${body}`.trim();
        this.apiKeyLoading = false;
      }
    });
  }

  // ---- Encrypt key by Config Server (admin-only) ----
  encryptByConfigServer(): void {
    const url = `${environment.apiBaseUrl}/tools/encrypt`;
    const body = this.encryptInput || '';
    this.encryptLoading = true;
    this.encryptResultText = 'Encrypting...';
    this.http.post(url + `?endpoint=${encodeURIComponent(this.encryptUrl)}`, body, {
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8',
        'accept': '*/*'
      },
      observe: 'response',
      responseType: 'text'
    }).subscribe({
      next: (res) => {
        const status = (res as any)?.status ?? 200;
        const text = (res as any)?.body ?? '';
        this.encryptResultText = `HTTP ${status}\n${text}`;
        this.encryptLoading = false;
      },
      error: (err) => {
        const status = err?.status;
        const text = typeof err?.error === 'string' ? err.error : (err?.message || 'Error');
        this.encryptResultText = `HTTP ${status ?? ''}\n${text}`.trim();
        this.encryptLoading = false;
      }
    });
  }

  decryptByConfigServer(): void {
    const url = `${environment.apiBaseUrl}/tools/decrypt`;
    const body = this.decryptInput || '';
    this.decryptLoading = true;
    this.decryptResultText = 'Decrypting...';
    this.http.post(url + `?endpoint=${encodeURIComponent(this.decryptUrl)}`, body, {
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8',
        'accept': '*/*'
      },
      observe: 'response',
      responseType: 'text'
    }).subscribe({
      next: (res) => {
        const status = (res as any)?.status ?? 200;
        const text = (res as any)?.body ?? '';
        this.decryptResultText = `HTTP ${status}\n${text}`;
        this.decryptLoading = false;
      },
      error: (err) => {
        const status = err?.status;
        const text = typeof err?.error === 'string' ? err.error : (err?.message || 'Error');
        this.decryptResultText = `HTTP ${status ?? ''}\n${text}`.trim();
        this.decryptLoading = false;
      }
    });
  }

  login(): void {
    this.auth.login();
  }

  logOut(): void {
    this.auth.logOut();
  }
}
